import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/utils';

const drawerVariants = cva(
  'relative bg-white rounded-t-2xl shadow-xl transform transition-transform duration-300 ease-out overflow-hidden flex flex-col',
  {
    variants: {
      size: {
        sm: 'w-full max-w-sm mx-auto max-h-[50vh]',
        md: 'w-full max-w-md mx-auto max-h-[70vh]',
        lg: 'w-full max-w-lg mx-auto max-h-[80vh]',
        xl: 'w-full max-w-xl mx-auto max-h-[90vh]',
        jumbo: 'w-full h-full',
        fullWidth: 'w-full max-h-[90vh]',
        fullPage: 'w-full h-full max-h-full rounded-none',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

export interface SimpleDrawerProps extends VariantProps<typeof drawerVariants> {
  /**
   * Whether the drawer is open
   */
  isOpen: boolean;
  /**
   * Callback when drawer should close
   */
  onClose: () => void;
  /**
   * Title of the drawer
   */
  title?: string;
  /**
   * Size variant of the drawer
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'fullWidth' | 'fullPage' | 'jumbo';
  /**
   * Additional CSS classes for the drawer content
   */
  className?: string;
  /**
   * Children content to render inside the drawer
   */
  children: React.ReactNode;
  /**
   * Whether to show the header
   */
  showHeader?: boolean;
}

const SimpleDrawer = React.forwardRef<HTMLDivElement, SimpleDrawerProps>(
  (
    { isOpen, onClose, title, size, className, children, showHeader = true },
    ref
  ) => {
    // Animation state management
    const [isVisible, setIsVisible] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);

    // Handle opening/closing animation
    React.useEffect(() => {
      const handleOpen = () => {
        setIsVisible(true);
        const timer = setTimeout(() => setIsAnimating(true), 10);
        return timer;
      };

      const handleClose = () => {
        setIsAnimating(false);
        const timer = setTimeout(() => setIsVisible(false), 300);
        return timer;
      };

      const timer = isOpen ? handleOpen() : handleClose();
      return () => clearTimeout(timer);
    }, [isOpen]);

    // Handle escape key and body scroll
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }, [isOpen, onClose]);

    if (!isVisible) return null;

    return (
      <div
        ref={ref}
        className={cn(
          'fixed inset-0 z-50 flex items-end justify-center',
          'transition-opacity duration-300',
          isAnimating ? 'opacity-100' : 'opacity-0'
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'drawer-title' : undefined}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClose();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Close drawer"
        />

        {/* Drawer content */}
        <div
          className={cn(
            drawerVariants({ size }),
            isAnimating ? 'translate-y-0' : 'translate-y-full',
            className
          )}
          onClick={e => e.stopPropagation()}
          role="presentation"
        >
          {/* Header */}
          {!!showHeader && (
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              {title ? (
                <h2
                  id="drawer-title"
                  className="text-lg font-semibold text-gray-900"
                >
                  {title}
                </h2>
              ) : null}
              <button
                onClick={onClose}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                aria-label="Close drawer"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">{children}</div>
        </div>
      </div>
    );
  }
);

SimpleDrawer.displayName = 'SimpleDrawer';

export interface SimpleDrawerTriggerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The drawer component to render when triggered
   */
  drawer: React.ReactElement<SimpleDrawerProps>;
  /**
   * Children content to render as the trigger
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes for the trigger container
   */
  className?: string;
}

const SimpleDrawerTrigger = React.forwardRef<
  HTMLDivElement,
  SimpleDrawerTriggerProps
>(({ drawer, children, className, ...props }, ref) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  // Clone the drawer element and pass the isOpen state and onClose handler
  const drawerElement = React.isValidElement(drawer)
    ? React.cloneElement(drawer, {
        isOpen,
        onClose: () => setIsOpen(false),
      })
    : drawer;

  return (
    <div ref={ref} className={cn('inline-block', className)} {...props}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className="cursor-pointer"
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {children}
      </div>
      {drawerElement}
    </div>
  );
});

SimpleDrawerTrigger.displayName = 'SimpleDrawerTrigger';

export { SimpleDrawer, SimpleDrawerTrigger };
export default SimpleDrawer;
